<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import PrintButton from '@/components/common/PrintButton'
import LeagueSections from '@/components/widgets/public/LeagueSections.vue'
import { useLeaguesStore } from '@/store/public/leagues'
import { getErrorMessage } from '@/utils/error-handling'

const leagues = useLeaguesStore()
const route = useRoute()

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

onMounted(async () => {
  errorMessage.value = null
  isLoading.value = true

  try {
    await leagues.selectLeagueById(route.params.id as string)
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching leagues: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
})
</script>

<template>
  <h1 v-if="isLoading" class="busy-indicator-container">
    <BusyIndicator />
  </h1>

  <NotificationBanner
    v-else-if="errorMessage"
    :message="errorMessage"
    is-danger
  />

  <NotificationBanner
    v-else-if="(leagues.selectedLeague?.sections ?? []).length === 0"
    :message="$t('leagues.errors.no-sections')"
    is-warning
  />

  <template v-else>
    <div class="league-standings-container">
      <h2 v-if="leagues.selectedLeague?.name" class="noprint">
        {{ leagues.selectedLeague?.name }}
      </h2>
      <p v-if="leagues.selectedLeague?.description">
        {{ leagues.selectedLeague?.description }}
      </p>
      <div class="table-controls">
        <PrintButton />
        <RouterLink
          :to="{
            path: `/leagues/${leagues.selectedSeason}/${leagues.selectedLeague?.id}/standings`,
          }"
          class="standings-link noprint"
        >
          <small>
            <FontAwesomeIcon class="icon" icon="file-contract" />
            <span>{{ $t('leagues.labels.standings') }}</span>
          </small>
        </RouterLink>
      </div>
    </div>

    <LeagueSections />
  </template>
</template>

<style lang="scss" scoped>
.busy-indicator-container {
  text-align: center;
}

.league-standings-container {
  .icon {
    margin-right: 0.25em;
  }
}
</style>
